import type { ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';

import { useAuthUser } from '@/auth/useAuthUser';
import { Logo } from '@/layout/Logo';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Requiere alguno de estos módulos en el claim `modules` del JWT. */
  requireModule?: string;
  /** Requiere alguno de estos roles. */
  requireAnyRole?: ReadonlyArray<string>;
}

export function ProtectedRoute({
  children,
  requireModule,
  requireAnyRole,
}: ProtectedRouteProps): ReactNode {
  const auth = useAuth();
  const user = useAuthUser();

  if (auth.isLoading) {
    return <FullScreenStatus title="Cargando sesión…" />;
  }

  if (auth.error) {
    return (
      <FullScreenStatus
        title="Error de autenticación"
        detail={auth.error.message}
        variant="error"
      />
    );
  }

  if (!auth.isAuthenticated) {
    void auth.signinRedirect();
    return <FullScreenStatus title="Redirigiendo al login…" />;
  }

  if (!user) {
    return <FullScreenStatus title="Sesión inválida" variant="error" />;
  }

  if (requireModule && !user.modules.includes(requireModule)) {
    return (
      <FullScreenStatus
        title="Módulo no activado"
        detail={`Tu tenant no tiene el módulo «${requireModule}» activado.`}
        variant="warning"
      />
    );
  }

  if (requireAnyRole && requireAnyRole.length > 0) {
    const has = requireAnyRole.some((r) => user.roles.includes(r));
    if (!has) {
      return (
        <FullScreenStatus
          title="Sin permisos suficientes"
          detail={`Roles requeridos: ${requireAnyRole.join(', ')}`}
          variant="warning"
        />
      );
    }
  }

  return <>{children}</>;
}

function FullScreenStatus({
  title,
  detail,
  variant = 'info',
}: {
  title: string;
  detail?: string;
  variant?: 'info' | 'warning' | 'error';
}): ReactNode {
  const color =
    variant === 'error'
      ? 'text-red-700'
      : variant === 'warning'
        ? 'text-amber-700'
        : 'text-brand-600';
  const ring =
    variant === 'error'
      ? 'ring-red-200'
      : variant === 'warning'
        ? 'ring-amber-200'
        : 'ring-brand-200';
  return (
    <div className="flex h-screen items-center justify-center bg-gradient-to-br from-white via-slate-50 to-brand-50 p-8">
      <div
        className={`flex flex-col items-center rounded-2xl bg-white px-10 py-12 shadow-xl ring-1 ${ring}`}
      >
        <Logo variant="full" size={64} className="mb-8" />
        <p className={`text-lg font-semibold ${color}`}>{title}</p>
        {detail && <p className="mt-2 max-w-md text-center text-sm text-slate-600">{detail}</p>}
        {variant === 'info' && !detail && (
          <div className="mt-6 h-1.5 w-32 overflow-hidden rounded-full bg-slate-200">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-brand-400" />
          </div>
        )}
      </div>
    </div>
  );
}
